import React from "react";
import { Carousel } from "react-responsive-carousel";
import Box from "@mui/material/Box";

type Props = {
  photos: string[];
  autoPlay?: boolean;
  interval?: number;
  transitionTime?: number;
  showArrows?: boolean;
};

export default function MultiCarousel({
  photos,
  autoPlay = true,
  interval = 4000,
  transitionTime = 600,
  showArrows = false,
}: Props) {
  // Always show 2 photos at a time
  const itemsPerSlide = 2;

  const n = photos.length || 0;
  if (n === 0) return null;

  // If we have <= itemsPerSlide photos just render them centered (no carousel looping/duplication)
  if (n <= itemsPerSlide) {
    const imageMaxHeight = { xs: "32vh", sm: "40vh", md: "48vh", lg: "60vh" };

    return (
      <Box
        sx={{
          display: "flex",
          gap: { xs: 1, sm: 1.5, md: 1 },
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
          maxWidth: "100%",
        }}
      >
        {photos.map((photo, i) => (
          <Box
            key={i}
            sx={{
              flex: "0 0 50%",
              maxHeight: imageMaxHeight,
              width: "auto",
              maxWidth: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Box
              component="img"
              src={photo}
              alt={`static-img-${i}`}
              sx={{
                maxHeight: imageMaxHeight,
                height: "auto",
                width: "auto",
                maxWidth: "100%",
                objectFit: "cover",
                borderRadius: 2,
              }}
            />
          </Box>
        ))}
      </Box>
    );
  }

  // Create slides that shift by one image each time.
  // Each slide contains exactly `itemsPerSlide` photos: [i, (i+1), ..., (i+itemsPerSlide-1)]
  const slidesIndexes: number[][] = Array.from({ length: n }, (_, i) =>
    Array.from({ length: itemsPerSlide }, (_, k) => (i + k) % n)
  );

  const imageMaxHeight = { xs: "32vh", sm: "40vh", md: "48vh", lg: "60vh" };

  return (
    <Carousel
      showArrows={showArrows}
      showStatus={false}
      showThumbs={false}
      infiniteLoop={true}
      autoPlay={autoPlay}
      interval={interval}
      transitionTime={transitionTime}
      swipeable={true}
      emulateTouch={true}
      dynamicHeight={false}
      // Keep centerMode off so the active slide (which itself contains 2 images) is perfectly centered
      centerMode={false}
      // Use slide animation for smooth left-shift transitions
      animationHandler="slide"
      // Hide indicators by default (optional)
      showIndicators={false}
    >
      {slidesIndexes.map((indices, idx) => {
        return (
          <div
            key={`slide-${idx}`}
            style={{
              padding: 10,
              // Ensure the slide content is centered horizontally
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Box
              sx={{
                display: "flex",
                gap: { xs: 1, sm: 1.5, md: 1 }, // Responsive gap between images
                justifyContent: "center",
                alignItems: "center",
                width: "100%",
                maxWidth: "100%",
              }}
            >
              {indices.map((photoIndex, i) => {
                const photo = photos[photoIndex];

                return (
                  <Box
                    key={`slide-${idx}-img-${i}`}
                    sx={{
                      flex: "0 0 50%", // Each photo takes equal space (50% for 2 photos)
                      maxHeight: imageMaxHeight,
                      width: "auto",
                      maxWidth: "100%",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <Box
                      component="img"
                      src={photo}
                      alt={`slide-${idx}-img-${i}`}
                      sx={{
                        maxHeight: imageMaxHeight,
                        height: "auto",
                        width: "auto",
                        maxWidth: "100%",
                        objectFit: "cover",
                        borderRadius: 2,
                      }}
                    />
                  </Box>
                );
              })}
            </Box>
          </div>
        );
      })}
    </Carousel>
  );
}